import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Importar exif-parser
import * as ExifParser from 'exif-parser';

@Injectable({
  providedIn: 'root',
})
export class ExifToolService {
  
  constructor(private http: HttpClient) {}

  /**
   * Extrae metadatos de una imagen usando múltiples métodos para un análisis completo
   */
  public async extractMetadata(file: File): Promise<any> {
    try {
      // Verificar si el archivo es un JPEG antes de usar exif-parser
      const isJpeg = file.type === 'image/jpeg' || 
                    file.name.toLowerCase().endsWith('.jpg') || 
                    file.name.toLowerCase().endsWith('.jpeg');
      
      // Si no es JPEG, saltar directamente a exifReader
      if (!isJpeg) {
        const exifReader = require('exifreader');
        const tags = await exifReader.load(file);
        const formattedTags = this.formatExifReaderTags(tags);
        formattedTags.manipulationAnalysis = this.analyzeForManipulation(formattedTags);
        return formattedTags;
      }
      
      // Para archivos JPEG, continuar con el análisis de exif-parser
      const arrayBuffer = await this.fileToArrayBuffer(file);
      
      // Análisis con exif-parser (más simple pero funcional)
      const parser = ExifParser.create(arrayBuffer);
      const exifResult = parser.parse();
      
      // También procesar con exifReader para comparar resultados
      const exifReader = require('exifreader');
      const exifReaderTags = await exifReader.load(file);
      
      // Combinar los resultados de ambos parsers para un análisis más completo
      const combinedMetadata = this.combineExifData(exifResult, exifReaderTags);
      
      // Añadir análisis de manipulación
      combinedMetadata.manipulationAnalysis = this.analyzeForManipulation(combinedMetadata);
      
      return combinedMetadata;
    } catch (error) {
      console.error('Error al extraer metadatos:', error);
      // Si falla, intentar solo con exifReader como fallback
      try {
        const exifReader = require('exifreader');
        const tags = await exifReader.load(file);
        const formattedTags = this.formatExifReaderTags(tags);
        formattedTags.manipulationAnalysis = this.analyzeForManipulation(formattedTags);
        return formattedTags;
      } catch (fallbackError) {
        return { 
          error: 'Error al procesar metadatos EXIF',
          details: error.message 
        };
      }
    }
  }
  
  /**
   * Combina los datos de exif-parser y exifReader
   */
  private combineExifData(exifParserResult: any, exifReaderTags: any): any {
    const combinedData: any = {
      // Datos generales de exif-parser
      imageSize: {
        width: exifParserResult.imageSize?.width,
        height: exifParserResult.imageSize?.height
      },
      
      // Timestamps
      createdAt: exifParserResult.tags?.DateTimeOriginal 
                 ? new Date(exifParserResult.tags.DateTimeOriginal * 1000).toISOString()
                 : null,
      modifiedAt: exifParserResult.tags?.ModifyDate
                  ? new Date(exifParserResult.tags.ModifyDate * 1000).toISOString()
                  : null,
                  
      // Datos de geolocalización
      gps: exifParserResult.tags?.GPSLatitude
           ? {
               latitude: exifParserResult.tags.GPSLatitude,
               longitude: exifParserResult.tags.GPSLongitude,
               altitude: exifParserResult.tags.GPSAltitude,
             }
           : null,
           
      // Datos del dispositivo
      device: {
        make: exifParserResult.tags?.Make || null,
        model: exifParserResult.tags?.Model || null,
        software: exifParserResult.tags?.Software || null,
      },
      
      // Configuración de la cámara
      camera: {
        exposureTime: exifParserResult.tags?.ExposureTime || null,
        fNumber: exifParserResult.tags?.FNumber || null,
        iso: exifParserResult.tags?.ISO || null,
        focalLength: exifParserResult.tags?.FocalLength || null,
        flash: exifParserResult.tags?.Flash || null,
      },
      
      // Datos específicos de la imagen
      image: {
        orientation: exifParserResult.tags?.Orientation || null,
        xResolution: exifParserResult.tags?.XResolution || null,
        yResolution: exifParserResult.tags?.YResolution || null,
        resolutionUnit: exifParserResult.tags?.ResolutionUnit || null,
        colorSpace: exifParserResult.tags?.ColorSpace || null,
      },
      
      // Datos de exifReader (formato más detallado)
      exifDetail: {},
      
      // Datos raw para análisis avanzado
      rawExifParser: exifParserResult,
    };
    
    // Añadir datos de exifReader que pueden ser más completos
    for (const [key, value] of Object.entries(exifReaderTags)) {
      if (value && typeof value === 'object' && 'description' in value) {
        if (!combinedData.exifDetail) {
          combinedData.exifDetail = {};
        }
        combinedData.exifDetail[key] = value.description;
      }
    }
    
    // Buscar software de edición
    if (combinedData.device.software) {
      const editingSoftware = ['photoshop', 'lightroom', 'gimp', 'illustrator', 'affinity', 
                               'capture one', 'luminar', 'ai'];
      
      const softwareLower = combinedData.device.software.toLowerCase();
      const hasEditingSoftware = editingSoftware.some(s => softwareLower.includes(s));
      
      if (hasEditingSoftware) {
        combinedData.editingSoftwareDetected = true;
        combinedData.editingSoftwareName = combinedData.device.software;
      }
    }
    
    // Detectar modificaciones
    if (combinedData.createdAt && combinedData.modifiedAt) {
      const createDate = new Date(combinedData.createdAt);
      const modifyDate = new Date(combinedData.modifiedAt);
      
      if (!isNaN(createDate.getTime()) && !isNaN(modifyDate.getTime())) {
        const diffMinutes = Math.abs((modifyDate.getTime() - createDate.getTime()) / (1000 * 60));
        
        if (diffMinutes > 5) {
          combinedData.timeElapsedBetweenCreationAndModification = diffMinutes;
          combinedData.significantTimeGap = true;
        }
      }
    }
    
    return combinedData;
  }
  
  /**
   * Formatea las etiquetas de exifReader en un formato más amigable
   */
  private formatExifReaderTags(tags: any): any {
    const formatted: any = {
      exifDetail: {},
    };
    
    for (const [key, value] of Object.entries(tags)) {
      if (value && typeof value === 'object' && 'description' in value) {
        formatted.exifDetail[key] = value.description;
        
        // También copiar propiedades más importantes al nivel superior
        if (['Make', 'Model', 'Software'].includes(key)) {
          formatted[key] = value.description;
        }
      }
    }
    
    return formatted;
  }

  /**
   * Convierte un archivo a ArrayBuffer
   */
  private fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Analiza los metadatos para buscar señales de manipulación
   */
  public analyzeForManipulation(metadata: any): { hasManipulationSigns: boolean; indicators: string[] } {
    const indicators: string[] = [];
    
    // Lista de software de edición conocido
    const editingSoftware = [
      'photoshop', 'lightroom', 'gimp', 'illustrator', 'affinity', 'capture one',
      'luminar', 'ai', 'intelligence', 'neural', 'edit', 'edited',
    ];
    
    // Verificar software usado desde el dispositivo o exifDetail
    const softwareToCheck = [
      metadata.device?.software,
      metadata.Software,
      metadata.exifDetail?.Software,
    ].filter(Boolean);
    
    for (const software of softwareToCheck) {
      const softwareLower = software.toLowerCase();
      if (editingSoftware.some(s => softwareLower.includes(s))) {
        indicators.push(`Imagen procesada con software: ${software}`);
        break;
      }
    }
    
    // Verificar si se detectó software de edición
    if (metadata.editingSoftwareDetected) {
      indicators.push(`Software de edición detectado: ${metadata.editingSoftwareName}`);
    }
    
    // Verificar diferencia significativa entre creación y modificación
    if (metadata.significantTimeGap) {
      indicators.push(`Tiempo entre creación y modificación: ${Math.round(metadata.timeElapsedBetweenCreationAndModification)} minutos`);
    }
    
    // Verificar presencia de datos de Photoshop
    const hasPhotoshopData = Object.keys(metadata.exifDetail || {}).some(key => 
      key.includes('Photoshop') || key.includes('photoshop')
    );
    
    if (hasPhotoshopData) {
      indicators.push('Datos de Photoshop detectados');
    }
    
    // Verificar presencia de datos XMP (comunes en imágenes editadas)
    const hasXmpData = Object.keys(metadata.exifDetail || {}).some(key => 
      key.toLowerCase().includes('xmp') || key.toLowerCase().includes('xap')
    );
    
    if (hasXmpData) {
      indicators.push('Datos XMP presentes (posible edición)');
    }
    
    // Verificar ausencia de datos EXIF críticos del dispositivo
    const hasMakeModel = (metadata.device?.make && metadata.device?.model) || 
                        (metadata.exifDetail?.Make && metadata.exifDetail?.Model);
    
    if (!hasMakeModel) {
      indicators.push('Falta información del dispositivo (Make/Model)');
    }
    
    return {
      hasManipulationSigns: indicators.length > 0,
      indicators,
    };
  }
  
  /**
   * Obtiene la orientación de la imagen según sus datos EXIF
   */
  public getImageOrientation(metadata: any): number {
    // Valores estándar de orientación EXIF:
    // 1 = 0 grados: la imagen está en su orientación correcta
    // 3 = 180 grados: la imagen está rotada 180 grados
    // 6 = 90 grados en sentido horario: la imagen está rotada 90 grados en sentido horario
    // 8 = 270 grados en sentido horario: la imagen está rotada 90 grados en sentido antihorario
    
    const orientation = metadata.image?.orientation || 
                        metadata.rawExifParser?.tags?.Orientation ||
                        metadata.exifDetail?.Orientation;
    
    if (!orientation) return 1; // Valor por defecto
    
    // Convertir a número si es string
    const orientationValue = typeof orientation === 'string' 
      ? parseInt(orientation, 10) 
      : orientation;
    
    return isNaN(orientationValue) ? 1 : orientationValue;
  }
  
  /**
   * Convierte los datos GPS de EXIF a coordenadas legibles
   */
  public formatGpsCoordinates(metadata: any): { latitude: string; longitude: string; altitude: string } | null {
    if (!metadata.gps?.latitude || !metadata.gps?.longitude) {
      return null;
    }
    
    return {
      latitude: `${Math.abs(metadata.gps.latitude).toFixed(6)}° ${metadata.gps.latitude >= 0 ? 'N' : 'S'}`,
      longitude: `${Math.abs(metadata.gps.longitude).toFixed(6)}° ${metadata.gps.longitude >= 0 ? 'E' : 'W'}`,
      altitude: metadata.gps.altitude ? `${metadata.gps.altitude.toFixed(2)} m` : 'No disponible',
    };
  }
}
